import { useEffect, useRef } from 'react';
import { dispatchSync } from 'nike-re-framejs';
import { Collection, fromJS, List, Map, Set } from 'immutable';
import * as diff from 'nike-ui/src/common/push/diff';
import {
    indexById,
    indexGroupedAndOrdered,
    insertToOrderedIndex,
    OrderedIndex,
    orderedIndexToIdsList,
    removeFromOrderedIndex
} from '../indexingUtils';
import { PUSH_PATHS } from '../paths';
import { HybridTournamentOverviewSnapshot } from '../../dto/SnapshotEnvelope';
import { Delta, PushEntity, PushEvent, PushPayload } from '../../dto/PushEnvelope';
import { ReframeEventHandler } from '../../../../types/reframe';
import { nuiCtx } from '../../../../common/initialization';

export function useHybridTournamentsPush(tournamentIds: Set<string>) {
    const subscriptionRef = useRef(null);
    const previousTournamentIdsRef = useRef(Set<string>());

    useEffect(() => {
        dispatchSync(reset());

        subscriptionRef.current = nuiCtx.pushClient.subscription({
            onSnapshot: snapshots => snapshots.forEach(snapshot => dispatchSync(onSnapshot(snapshot))),
            onMessage: push => dispatchSync(onPush(push))
        });
        return () => subscriptionRef.current?.close();
    }, []);

    useEffect(() => {
        const previousTournamentIds = previousTournamentIdsRef.current || Set();
        const addedTournamentIds: Set<string> = (tournamentIds || Set()).subtract(previousTournamentIds);
        const removedTournamentIds: Set<string> = previousTournamentIds.subtract(tournamentIds || Set());
        if (addedTournamentIds.isEmpty() && removedTournamentIds.isEmpty()) {
            return () => {};
        }
        const paths = tournamentIds.map(tournamentId => PUSH_PATHS.hybridTournamentOverview(tournamentId)).toArray();
        subscriptionRef.current.resetPaths(paths);
        previousTournamentIdsRef.current = tournamentIds;
        return () => {
            removedTournamentIds.forEach(tournamentId => dispatchSync(removeTournament(tournamentId)));
        };
    }, [tournamentIds]);
}

function onSnapshot(snapshot: HybridTournamentOverviewSnapshot): ReframeEventHandler {
    return db =>
        db
            .mergeIn(['turboLive', 'overview', 'matches'], Map(), indexById(snapshot.matches))
            .mergeIn(['turboLive', 'overview', 'bets'], Map(), indexById(snapshot.bets))
            .mergeIn(['turboLive', 'overview', 'match2bets'], Map(), indexGroupedAndOrdered(snapshot.bets, 'matchId'))
            .mergeIn(
                ['turboLive', 'overview', 'tournament2matches'],
                Map(),
                indexGroupedAndOrdered(snapshot.matches, 'tournamentId', 'startTime')
            );
}

function onPush(payload: PushPayload): ReframeEventHandler {
    return db => {
        switch (payload.t) {
            case PushEntity.BET:
                return db
                    .updateIn(['turboLive', 'overview', 'match2bets'], indexGroupUpdateFn(payload, 'matchId', 'order'))
                    .updateIn(['turboLive', 'overview', 'bets'], indexUpdateFn(payload));
            case PushEntity.MATCH:
                return db
                    .updateIn(
                        ['turboLive', 'overview', 'tournament2matches'],
                        indexGroupUpdateFn(payload, 'tournamentId', 'startTime')
                    )
                    .updateIn(['turboLive', 'overview', 'matches'], indexUpdateFn(payload));
        }
    };
}

function reset(): ReframeEventHandler {
    return db =>
        db
            .setIn(['turboLive', 'overview', 'matches'], Map())
            .setIn(['turboLive', 'overview', 'bets'], Map())
            .setIn(['turboLive', 'overview', 'tournament2matches'], Map())
            .setIn(['turboLive', 'overview', 'match2bets'], Map());
}

function removeKeys(map: Map<string, any>, keys: Collection<number, string>) {
    return keys.reduce((acc, key) => acc.delete(key), map);
}

function removeTournament(tournamentId: string): ReframeEventHandler {
    return db => {
        const matchIds = orderedIndexToIdsList(
            db.getIn(['turboLive', 'overview', 'tournament2matches', tournamentId], List()) as List<OrderedIndex>
        );
        const betIds = matchIds.flatMap(matchId =>
            orderedIndexToIdsList(
                db.getIn(['turboLive', 'overview', 'match2bets', matchId], List()) as List<OrderedIndex>
            )
        );

        return db
            .updateIn(['turboLive', 'overview', 'match2bets'], Map(), (match2bets: Map<string, any>) =>
                removeKeys(match2bets, matchIds)
            )
            .updateIn(['turboLive', 'overview', 'tournament2matches'], Map(), (tournament2matches: Map<string, any>) =>
                tournament2matches.delete(tournamentId)
            )
            .updateIn(['turboLive', 'overview', 'matches'], Map(), (matches: Map<string, any>) =>
                removeKeys(matches, matchIds)
            )
            .updateIn(['turboLive', 'overview', 'bets'], Map(), (bets: Map<string, any>) => removeKeys(bets, betIds))
            .deleteIn(['turboLive', 'overview', 'bets', tournamentId]);
    };
}

function indexUpdateFn(payload: PushPayload) {
    return (entityById: Map<string, any>) => {
        switch (payload.e) {
            case PushEvent.CREATED:
                return entityById.set(payload.d.id, fromJS(payload.d));
            case PushEvent.UPDATED:
                const delta = payload.d as Delta;
                return entityById.update(payload.d.id, existing => diff.applyDelta(delta.diff, existing));
            case PushEvent.DELETED:
                return entityById.delete(payload.d.id);
            default:
                console.error(`Invalid PushEvent ${payload.e}`);
                return entityById;
        }
    };
}

function indexGroupUpdateFn(payload: PushPayload, groupAttr: string, orderAttr: string) {
    return (groupedIndex: Map<string, List<OrderedIndex>>) => {
        const id = payload.d.id;
        const order = payload.d[orderAttr];
        const groupId = payload.d[groupAttr];
        switch (payload.e) {
            case PushEvent.CREATED:
                return groupedIndex.update(groupId, List(), insertToOrderedIndex({ id, order }));
            case PushEvent.DELETED:
                return groupedIndex.update(groupId, List(), removeFromOrderedIndex(id));
            default:
                return groupedIndex;
        }
    };
}
